// frontend/src/components/upload/UploadContainer.jsx
import React, { useState, useCallback } from 'react';
import { Upload, FileText, CheckCircle, AlertTriangle, X } from 'lucide-react';
import UploadZone from './UploadZone';
import FileValidator from './FileValidator';
import ProgressIndicator from './ProgressIndicator';
import DataPreview from './DataPreview';
import QualityReport from './QualityReport';

const UploadContainer = ({ onUploadSuccess, onClose }) => {
  const [uploadState, setUploadState] = useState({
    file: null,
    uploading: false,
    progress: 0,
    validationResult: null,
    error: null,
    step: 'select' // 'select', 'uploading', 'validating', 'preview', 'complete'
  });

  const handleFileSelect = useCallback((file) => {
    setUploadState(prev => ({
      ...prev,
      file,
      error: null,
      step: 'uploading',
      progress: 0
    }));
    
    uploadFile(file);
  }, []);

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      setUploadState(prev => ({ ...prev, uploading: true }));

      // Simulation de progression d'upload
      const progressInterval = setInterval(() => {
        setUploadState(prev => {
          const newProgress = Math.min(prev.progress + Math.random() * 15, 85);
          return { ...prev, progress: newProgress };
        });
      }, 200);

      const response = await fetch('/api/v3/upload-load-curve', {
        method: 'POST',
        body: formData,
      });

      clearInterval(progressInterval);

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const result = await response.json();
      
      setUploadState(prev => ({
        ...prev,
        uploading: false,
        progress: 100,
        validationResult: result,
        step: result.quality_report.is_valid ? 'preview' : 'validation_error'
      }));

      // Délai pour voir la progression à 100%
      setTimeout(() => {
        setUploadState(prev => ({ ...prev, step: 'complete' }));
      }, 1000);

    } catch (error) {
      console.error('Erreur upload:', error);
      setUploadState(prev => ({
        ...prev,
        uploading: false,
        error: error.message,
        step: 'error'
      }));
    }
  };

  const resetUpload = useCallback(() => {
    setUploadState({
      file: null,
      uploading: false,
      progress: 0,
      validationResult: null,
      error: null,
      step: 'select'
    });
  }, []);

  const handleUseData = useCallback(() => {
    if (uploadState.validationResult && onUploadSuccess) {
      onUploadSuccess(uploadState.validationResult);
    }
  }, [uploadState.validationResult, onUploadSuccess]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 relative">
          <h2 className="text-2xl font-bold flex items-center gap-3">
            <Upload className="w-6 h-6" />
            Import de Courbe de Charge
          </h2>
          <p className="text-blue-100 mt-2">
            Téléchargez votre fichier CSV pour analyse TURPE experte
          </p>
          
          {/* Bouton fermer */}
          {onClose && (
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-white hover:text-gray-200 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          )}
        </div>

        {/* Contenu principal */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          
          {/* Étape 1: Sélection de fichier */}
          {uploadState.step === 'select' && (
            <UploadZone onFileSelect={handleFileSelect} />
          )}

          {/* Étape 2: Upload en cours */}
          {(uploadState.step === 'uploading' || uploadState.uploading) && (
            <div className="text-center py-12">
              <ProgressIndicator 
                progress={uploadState.progress}
                status="upload"
                fileName={uploadState.file?.name}
              />
            </div>
          )}

          {/* Étape 3: Validation en cours */}
          {uploadState.step === 'validating' && (
            <div className="text-center py-12">
              <FileValidator />
            </div>
          )}

          {/* Étape 4: Erreur de validation */}
          {uploadState.step === 'validation_error' && uploadState.validationResult && (
            <div className="space-y-6">
              <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                  <h3 className="text-lg font-semibold text-red-800">
                    Problèmes détectés dans les données
                  </h3>
                </div>
                <QualityReport report={uploadState.validationResult.quality_report} />
              </div>
              
              <div className="flex gap-3 justify-end">
                <button
                  onClick={resetUpload}
                  className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Essayer un autre fichier
                </button>
                <button
                  onClick={() => setUploadState(prev => ({ ...prev, step: 'preview' }))}
                  className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                >
                  Continuer malgré tout
                </button>
              </div>
            </div>
          )}

          {/* Étape 5: Aperçu des données */}
          {uploadState.step === 'preview' && uploadState.validationResult && (
            <div className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                  <h3 className="text-lg font-semibold text-green-800">
                    Données validées avec succès
                  </h3>
                </div>
              </div>

              <DataPreview 
                statistics={uploadState.validationResult.statistics}
                preview={uploadState.validationResult.data_preview}
                qualityReport={uploadState.validationResult.quality_report}
              />
              
              <div className="flex gap-3 justify-end">
                <button
                  onClick={resetUpload}
                  className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Choisir un autre fichier
                </button>
                <button
                  onClick={handleUseData}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  Utiliser ces données
                </button>
              </div>
            </div>
          )}

          {/* Étape 6: Erreur */}
          {uploadState.step === 'error' && (
            <div className="text-center py-12">
              <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Erreur lors du téléchargement
              </h3>
              <p className="text-gray-600 mb-6">
                {uploadState.error}
              </p>
              <button
                onClick={resetUpload}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Réessayer
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadContainer;
